import React, { useCallback, useEffect } from 'react';
import { Box } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { InterveneSocketProvider } from '../sockets';
import useChannels from '../hooks/useChannels';
import useUserList from '../hooks/useUserList';
import useChatPanel from '../hooks/useChatPanel';
import { useInterveneSocketContext } from '../sockets';
import useInterveneHeaderActions from '../hooks/useInterveneHeaderActions';
import useProfilePanel from '../hooks/useProfilePanel';
import useInterveneWorkspaceLayout from '../hooks/useInterveneWorkspaceLayout';
import useUserProfile from '../hooks/useUserProfile';
import useLeadFormContext from '../hooks/useLeadFormContext';
import UserList from '../components/UserList';
import ChatPanel from '../components/chat/ChatPanel';
import CustomerContextDrawer from '../components/context-drawer/CustomerContextDrawer';
import DialerCallDialog from '../components/DialerCallDialog';
import InterveneTemplateDialog from '../components/InterveneTemplateDialog';
import { interveneFilterAtom } from '../state/interveneAtoms';
import useInterveneSx from '../hooks/useInterveneSx';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider';
import useInterveneMessageSoundUnlock from '../hooks/useInterveneMessageSound';
import useMobileChatNav from '../hooks/useMobileChatNav';

function IntervenePageContent() {
  useInterveneMessageSoundUnlock();
  const interveneSx = useInterveneSx();
  const { sendMarkRead } = useInterveneSocketContext();
  const {
    users, chatCounts, filters, isLoading,
    handleTabChange, handleSearch, handleScroll, handleUserSelect,
    switchToTabPreservingChat, refreshChatCounts,
  } = useUserList();

  const {
    isSuperAdmin,
    hasSelectedOrganisation,
    hasActiveChannel,
    activeOrganisation,
    organisationId,
    channels,
    isLoadingChannels,
    activeChannel,
    handleOrganisationSelect,
    handleChannelSelect,
  } = useChannels();

  const {
    activeUser, messages, isLoadingMessages, isLoadingOlder, hasMoreMessages,
    draftMessage, setDraftMessage, isSending,
    loadOlderMessages,
    shouldStickToBottomRef, handleSendMessage, handleTemplateSent, toggleSession,
  } = useChatPanel({ switchToTabPreservingChat, refreshChatCounts });

  const globalFilters = useRecoilValue(interveneFilterAtom);

  useEffect(() => {
    if (!activeUser?.phone_number || !globalFilters.botNumber) return;
    sendMarkRead?.();
  }, [activeUser?.phone_number, globalFilters.botNumber, sendMarkRead]);

  const {
    callDialogOpen,
    templateDialogOpen,
    openCallDialog,
    openTemplateDialog,
    closeCallDialog,
    closeTemplateDialog,
  } = useInterveneHeaderActions();

  const {
    isOpen: isDrawerOpen,
    activeTab,
    close: closeDrawer,
    setTab,
    toggle: toggleProfile,
    isInlineLayout,
  } = useProfilePanel(activeUser?.phone_number);

  useInterveneWorkspaceLayout(isDrawerOpen);

  const { journey, isLoadingProfile } = useUserProfile({ isProfileOpen: isDrawerOpen });

  const leadFormContext = useLeadFormContext({
    phone: activeUser?.phone_number,
    isDrawerOpen,
    organisationId,
  });

  const {
    showMobileChat,
    showMobileList,
    openMobileChat,
    backToUserList,
  } = useMobileChatNav(activeUser?.phone_number, closeDrawer);

  const handleSelectUser = useCallback(
    (user) => {
      handleUserSelect(user);
      openMobileChat();
    },
    [handleUserSelect, openMobileChat]
  );

  return (
    <Box sx={interveneSx.layoutShell}>
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            height: '100%',
            alignItems: 'stretch',
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: { xs: '100%', md: 400, xl: 420 },
              flex: { xs: '1 1 auto', md: '0 0 auto' },
              overflow: 'hidden',
              borderRight: { md: '1px solid' },
              borderColor: { md: 'divider' },
              display: { xs: showMobileList ? 'flex' : 'none', md: 'flex' },
              flexDirection: 'column',
              alignSelf: 'stretch',
              '& > *': {
                flex: 1,
                minHeight: 0,
                height: '100%',
              },
            }}
          >
            <UserList
              users={users}
              chatCounts={chatCounts}
              filters={filters}
              isLoading={isLoading}
              activeUser={activeUser}
              onTabChange={handleTabChange}
              onSearch={handleSearch}
              onScroll={handleScroll}
              onSelectUser={handleSelectUser}
              channels={channels}
              activeChannel={activeChannel}
              onChannelSelect={handleChannelSelect}
              isLoadingChannels={isLoadingChannels}
              isSuperAdmin={isSuperAdmin}
              activeOrganisation={activeOrganisation}
              onOrganisationSelect={handleOrganisationSelect}
              hasSelectedOrganisation={hasSelectedOrganisation}
              hasActiveChannel={hasActiveChannel}
            />
          </Box>

          <Box
            sx={{
              height: '100%',
              minWidth: 0,
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: {
                xs: showMobileChat ? 'flex' : 'none',
                md: 'flex',
              },
              flexDirection: 'column',
              '& > *': {
                flex: 1,
                minHeight: 0,
                width: '100%',
              },
            }}
          >
            <ChatPanel
              activeUser={activeUser}
              messages={messages}
              isLoadingMessages={isLoadingMessages}
              isLoadingOlder={isLoadingOlder}
              hasMoreMessages={hasMoreMessages}
              draftMessage={draftMessage}
              onDraftChange={setDraftMessage}
              isSending={isSending}
              onSendMessage={handleSendMessage}
              onToggleSession={toggleSession}
              onLoadOlderMessages={loadOlderMessages}
              shouldStickToBottomRef={shouldStickToBottomRef}
              userType={filters.userType}
              onCall={openCallDialog}
              onSendTemplate={openTemplateDialog}
              onToggleProfile={toggleProfile}
              isProfileOpen={isDrawerOpen}
              onBackToList={showMobileChat ? backToUserList : undefined}
            />
          </Box>

          <CustomerContextDrawer
            open={isDrawerOpen}
            activeTab={activeTab}
            onTabChange={setTab}
            onClose={closeDrawer}
            activeUser={activeUser}
            journey={journey}
            isLoadingProfile={isLoadingProfile}
            isInlineLayout={isInlineLayout}
            organisationId={organisationId}
            leadFormContext={leadFormContext}
          />
        </Box>

        <DialerCallDialog
          open={callDialogOpen}
          onClose={closeCallDialog}
          userDetails={activeUser}
        />

        <InterveneTemplateDialog
          open={templateDialogOpen}
          onClose={closeTemplateDialog}
          activeUser={activeUser}
          botNumber={globalFilters.botNumber}
          channelObj={globalFilters.channelObj}
          organisationId={globalFilters.organisationId}
          onTemplateSent={handleTemplateSent}
        />
      </Box>
  );
}

/**
 * Smart Controller Page — connects hooks to presentation components.
 */
export default function IntervenePage() {
  return (
    <FeatureThemeProvider sx={{ flex: 1, minHeight: 0, height: '100%' }}>
      <InterveneSocketProvider>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            height: '100%',
          }}
        >
          <IntervenePageContent />
        </Box>
      </InterveneSocketProvider>
    </FeatureThemeProvider>
  );
}
